import { invalidateConfigQueries } from "@/lib/config-pagination";
import axios from "axios";
import { resolveOrgResumedOwner, sameOrgUnit } from "@/lib/org-resume";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { isConversationEnded } from "@/components/agents/conversation/conversationReducer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { QueueControlContext } from "@/components/agents/conversation/QueueControlContext";
import {
  conversationFailure,
  type ConversationStatus,
} from "@/api/agent-conversation";
import {
  fetchOrgScriptOffer,
  orgStructureConversation,
  type OrgScriptChoice,
  type OrgScriptMode,
  type OrgScriptOffer,
} from "@/api/org-structure-conversation";
import {
  listRunnableActivityCodes,
  type RunnableCodesResponse,
  type ConfigActivityRow,
  type ActivityNode,
  type ConfigNode,
  type ConfigServerOverview,
} from "@/api/config-server-stages";
import {
  openNodeConversation,
  openOrgUnitConversation,
} from "@/api/stage-conversations";
import { type ScriptModeRequest } from "@/components/agents/configuration-server/OrgStructureTree";
import { OrgExecutionTree } from "@/components/agents/configuration-server/stages/OrgExecutionTree";
import {
  getOrgRunnableKeys,
  getOrgStatuses,
  getOrgUnitContext,
  type OrgRunKey,
} from "@/api/org-execution";
import { orgNodeFromContext } from "@/lib/org-unit-row";
import { LIST_QUERY_OPTIONS } from "@/lib/config-pagination";
import { SscuiActivityModal } from "@/components/agents/configuration-server/SscuiActivityModal";
import { type ConfigActivityEntry } from "@/components/agents/configuration-server/stages/activityEntries";
import { runBlockedReason } from "@/components/agents/configuration-server/stages/centralState";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { ExecutionActivityArea } from "@/components/agents/configuration-server/stages/ExecutionActivityArea";
import { ConfigActivityList } from "@/components/agents/configuration-server/stages/ConfigActivityList";
import { OrgStructureSyncAlert } from "@/components/agents/configuration-server/stages/OrgStructureSyncAction";
import { StageAlert } from "@/components/agents/configuration-server/stages/StageAlert";
import { GrowConnectionDialog } from "@/components/agents/configuration-server/stages/ConnectionDialogs";
import { ConfigRunAllControls } from "@/components/agents/configuration-server/stages/ConfigRunAllControls";
import { ConfigServerAlert } from "@/components/agents/configuration-server/ConfigServerAlert";
import { ExecutionPanel } from "@/components/agents/configuration-server/stages/ExecutionPanel";
import { ScriptOfferPanel } from "@/components/agents/configuration-server/stages/ScriptOfferPanel";
import {
  LeadNumber,
  StageFooter,
  StageHeader,
  StageNavButtons,
} from "@/components/agents/configuration-server/stages/stageNav";
import { stageStep } from "@/components/agents/configuration-server/stages/stageOrder";
import { StageLoading } from "@/components/agents/configuration-server/stages/StageLoading";
import { conversationErrorText } from "@/components/agents/conversation/conversationTexts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTargetConnection } from "@/hooks/useApi";
import { useFitAboveFooter } from "@/hooks/useFitAboveFooter";
import {
  stageKeys,
  useConfigActivities,
  useExecutionActivityAreas,
  useActivityStatuses,
  useConfigServerOverview,
} from "@/hooks/useConfigServerStages";
import {
  otherConversationOpenText,
  toastOpenError,
} from "@/lib/conversation-refusal";
import {
  effectiveOrgId,
  orgUnitTypeLabelKey,
  type OrgTreeNode,
} from "@/lib/org-structure-tree";
import { cn } from "@/lib/utils";

/** Ordem fixa (spec §9): a aba seguinte só libera com a anterior concluída. */
const TABS: { node: ConfigNode; label: string }[] = [
  { node: "org_structure", label: "Organizational structure" },
  { node: "sscui", label: "SSCUI configurations" },
  { node: "module_config", label: "App Fiori configurations" },
];

/** Fim de conversa que para a fila do "Run all" logo que a lista o mostra. */
const QUEUE_STOP_STATUSES: ReadonlySet<string> = new Set([
  "failed",
  "interrupted",
]);

/**
 * Fila do "Run all" (ruling E-8): o motor de conversas não tem fila, então a tela abre
 * uma entrada por vez. `current.entry` é a da conversa aberta pela fila (o código da
 * configuração nas abas de atividade, a unidade na estrutura organizacional), e
 * `current.runId` o run dela.
 */
interface ConfigQueue<T> {
  remaining: T[];
  current: { entry: T; runId: string } | null;
}

/** A vigia da conversa da vez: vista em voo, e a leitura da lista quando ela saiu da visão geral. */
interface QueueWatch {
  seenActive: boolean;
  ended: boolean;
  endedAt: number | undefined;
}

const freshQueueWatch = (): QueueWatch => ({
  seenActive: false,
  ended: false,
  endedAt: undefined,
});

/**
 * O que a fila faz com a conversa da vez. Concluída, falha ou interrompida abrem a
 * próxima: o "Run all" só espera a conversa que aguarda resposta. A que saiu da visão
 * geral e segue sem status final na primeira leitura da lista depois disso para a fila
 * (encerrada sem concluir, ou o item preso em `running`): a fila nunca fica parada em
 * silêncio.
 *
 * `status` é o do item relido com o run da vez, ou `null` se a lista ainda não o tem.
 */
function queueTurnVerdict(
  watch: QueueWatch,
  turn: { inFlight: boolean; status: string | null; listReadAt: number },
): "advance" | "stop" | "wait" {
  if (turn.inFlight) {
    watch.seenActive = true;
    watch.ended = false;
    return "wait";
  }
  // Terminou, falhou ou foi interrompida: a fila segue para a próxima. Só a conversa
  // esperando resposta (em voo, acima) a segura.
  if (turn.status === "completed") return "advance";
  if (turn.status !== null && QUEUE_STOP_STATUSES.has(turn.status))
    return "advance";
  if (!watch.seenActive) return "wait";
  if (!watch.ended) {
    watch.ended = true;
    watch.endedAt = turn.listReadAt;
    return "wait";
  }
  return turn.listReadAt !== watch.endedAt ? "stop" : "wait";
}

const TAB_TRIGGER_CLASS =
  "group gap-2 rounded-b-none rounded-t-md border-b-2 border-transparent px-4 py-3 font-semibold text-white/75 shadow-none data-[state=active]:border-[var(--exed-gold)] data-[state=active]:bg-[var(--exed-surface)] data-[state=active]:text-[var(--exed-ink-900)] data-[state=active]:shadow-none";
const TAB_PILL_CLASS =
  "rounded-full bg-white/[0.12] px-2 py-px font-mono text-xs font-semibold text-white group-data-[state=active]:bg-[rgba(255,200,61,0.25)] group-data-[state=active]:text-[#7a5600]";
const PANEL_CLASS =
  "rounded-xl border border-[var(--exed-card-border)] bg-white";

/** O servidor decide o estado de cada nó; item que falhou não conta como concluído. */
function nodeState(overview: ConfigServerOverview, node: ConfigNode) {
  return overview.stages.execution.by_node[node] ?? "not_started";
}

/** Abre na primeira aba liberada que ainda não terminou. */
function defaultTab(overview: ConfigServerOverview): ConfigNode {
  const open = TABS.find(
    ({ node }) => !["done", "locked"].includes(nodeState(overview, node)),
  );
  return open?.node ?? "org_structure";
}

/** A ordem ideal entre as abas; é só um aviso, nenhuma aba trava a outra. */
function orderTipMessage(t: TFunction): string {
  return t(
    "Ideally, finish the whole organizational structure first, then confirm and deploy it manually in the CBC.",
  );
}

export default function ExecutionStage() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return null;
  const centralHref = `/projects/${projectId}/agents/configuration-server`;
  return <ExecutionScreen projectId={projectId} centralHref={centralHref} />;
}

/** A conversa em voo de um nó, pela visão geral: o run, a chave e o nível do item (§9.1b). */
interface ActiveConversation {
  runId: string;
  code: string | null;
  itemType: string | null;
}

function ExecutionScreen({
  projectId,
  centralHref,
}: {
  projectId: string;
  centralHref: string;
}) {
  const { t } = useTranslation();
  const overviewQuery = useConfigServerOverview(projectId);
  const overview = overviewQuery.data;
  const [chosenTab, setChosenTab] = useState<ConfigNode | null>(null);
  const queryClient = useQueryClient();
  // O rodapé fixo: o painel do terminal mede a altura até o topo dele. Estado, não
  // ref-objeto: o rodapé monta depois do painel e a tela precisa remedir quando ele chega.
  const [footer, setFooter] = useState<HTMLDivElement | null>(null);

  const retryFailedReads = () => {
    // A lista da aba desmonta quando a visão geral falha. Releia também as
    // consultas de lista que falharam, inclusive as inativas com dado fresco no cache,
    // e a visão geral ao mesmo tempo: uma não espera pela outra.
    void Promise.all([
      queryClient.refetchQueries({
        queryKey: [...stageKeys.all(projectId), "activities"],
        type: "all",
        predicate: (query) => query.state.status === "error",
      }),
      overviewQuery.refetch(),
    ]);
  };

  // A execução em voo acabou ou deu lugar a outra (a visão geral faz polling enquanto
  // ela existe): relê a lista e os contadores, senão a configuração concluída segue
  // com o status antigo e ▶.
  const activeRun = overview?.active_run ?? null;
  const activeRunId = activeRun?.id ?? null;
  const previousActiveRunId = useRef(activeRunId);
  useEffect(() => {
    if (
      previousActiveRunId.current &&
      previousActiveRunId.current !== activeRunId
    ) {
      void invalidateConfigQueries(queryClient, stageKeys.overview(projectId));
      void invalidateConfigQueries(queryClient, [
        ...stageKeys.all(projectId),
        "activity-status",
      ]);
      void invalidateConfigQueries(queryClient, [
        ...stageKeys.all(projectId),
        "activities",
      ]);
      void invalidateConfigQueries(queryClient, ["migration-items", projectId]);
    }
    previousActiveRunId.current = activeRunId;
  }, [activeRunId, projectId, queryClient]);

  const execution = overview?.stages.execution;
  const unlocked =
    !!execution && execution.state !== "locked" && !overviewQuery.isError;
  const tab = chosenTab ?? (overview ? defaultTab(overview) : "org_structure");
  const blocked = runBlockedReason(overview, t);
  const conversationOf = (node: ConfigNode): ActiveConversation | null =>
    activeRun?.kind === "conversation" && activeRun.node === node
      ? {
          runId: activeRun.id,
          code: activeRun.activity_code ?? null,
          itemType: activeRun.item_type ?? null,
        }
      : null;

  // A aba anterior que ainda não terminou: o "Go to" do alerta leva até ela.
  const pendingBefore = (node: ConfigNode) => {
    const index = TABS.findIndex((candidate) => candidate.node === node);
    return (
      TABS.slice(0, index).find(
        (candidate) =>
          !overview || nodeState(overview, candidate.node) !== "done",
      ) ?? null
    );
  };

  // Nenhuma aba trava a outra: com a estrutura pendente, só o aviso da ordem ideal na aba
  // SSCUI. O App Fiori não avisa nada: a ordem ideal só vale para o SSCUI.
  const activityTab = (node: ActivityNode) => {
    const previous = node === "sscui" ? pendingBefore(node) : null;
    return (
      <ActivityExecution
        key={node}
        footer={footer}
        projectId={projectId}
        node={node}
        counts={overview?.stages.management.by_node[node]}
        blocked={blocked}
        live={!!activeRunId}
        conversation={conversationOf(node)}
        orderTip={
          previous && {
            message: orderTipMessage(t),
            goTo: {
              label: t(previous.label),
              onGo: () => setChosenTab(previous.node),
            },
          }
        }
      />
    );
  };

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setChosenTab(value as ConfigNode)}
      className="relative -m-6 flex min-h-[calc(100%+3rem)] flex-col"
    >
      <StageHeader
        number="03"
        title={t("Execution")}
        centralHref={centralHref}
        live={!!activeRun}
        tabs={
          unlocked &&
          overview && (
            <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-0 text-white">
              {TABS.map(({ node, label }) => {
                const counts = overview.stages.management.by_node[node];
                return (
                  <TabsTrigger
                    key={node}
                    value={node}
                    className={TAB_TRIGGER_CLASS}
                  >
                    <span>{t(label)}</span>
                    {counts && (
                      <span className={TAB_PILL_CLASS}>
                        {`${counts.completed.toLocaleString("en-US")}/${counts.approved.toLocaleString("en-US")}`}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          )
        }
      />

      <div className="mx-auto w-full max-w-[1488px] flex-1 px-6 pb-6 pt-5">
        {overviewQuery.isError ? (
          <ConfigServerAlert
            message={t("Could not load this page's data.")}
            onRetry={retryFailedReads}
          />
        ) : !overview || !execution ? (
          <StageLoading variant="page" />
        ) : execution.state === "locked" ? (
          <div className={`${PANEL_CLASS} p-6 text-sm`}>
            <p>
              {overview.stages.extraction.state !== "done"
                ? t("Unlocks when the extraction finishes")
                : t("Waiting for at least one approved item")}
            </p>
            <Link
              to={centralHref}
              className="mt-2 inline-block font-semibold text-[#1d4ea0] underline"
            >
              {t("Configuration Server")}
            </Link>
          </div>
        ) : (
          <>
            <TabsContent value="org_structure" className="mt-0">
              {tab === "org_structure" && (
                <>
                  <OrgStructureSyncAlert projectId={projectId} />
                  <OrgExecution
                    projectId={projectId}
                    blocked={blocked}
                    footer={footer}
                    conversation={conversationOf("org_structure")}
                    counts={overview?.stages.management.by_node.org_structure}
                  />
                </>
              )}
            </TabsContent>
            <TabsContent value="sscui" className="mt-0">
              {tab === "sscui" && activityTab("sscui")}
            </TabsContent>
            <TabsContent value="module_config" className="mt-0">
              {tab === "module_config" && activityTab("module_config")}
            </TabsContent>
          </>
        )}
      </div>

      {unlocked && overview && execution && (
        <ExecutionFooter
          overview={overview}
          tab={tab}
          centralHref={centralHref}
          footerRef={setFooter}
        />
      )}
    </Tabs>
  );
}

/**
 * Rodapé fixo (Claude Design): quantos a aba já executou, o estágio e os botões de voltar e
 * avançar entre os estágios. A Execução é o último: o Próximo fica desabilitado.
 */
function ExecutionFooter({
  overview,
  tab,
  centralHref,
  footerRef,
}: {
  overview: ConfigServerOverview;
  tab: ConfigNode;
  centralHref: string;
  footerRef: (element: HTMLDivElement | null) => void;
}) {
  const { t } = useTranslation();
  const current = TABS[TABS.findIndex((candidate) => candidate.node === tab)];
  const counts = overview.stages.management.by_node[tab];
  const done = (counts?.completed ?? 0).toLocaleString("en-US");
  const total = (counts?.approved ?? 0).toLocaleString("en-US");
  return (
    <StageFooter footerRef={footerRef}>
      <div className="flex flex-wrap items-center gap-3 text-[13px] text-[#596b85]">
        <span>
          <LeadNumber
            text={t("{{done}} of {{total}} executed in {{tab}}", {
              done,
              total,
              tab: t(current.label),
            })}
            lead={done}
            strongClassName="text-[#08254c]"
          />
        </span>
        <span aria-hidden="true" className="text-[#8a97ab]">
          ·
        </span>
        <span>{t("Step {{step}} of 3", { step: stageStep("execution") })}</span>
      </div>
      <StageNavButtons stage="execution" centralHref={centralHref} />
    </StageFooter>
  );
}

/**
 * Lista à esquerda, console à direita (Claude Design: 3 e 2, quebrando abaixo de ~956px).
 * O console termina acima do rodapé fixo, qualquer que seja a janela: o input e o
 * botão de enviar nunca ficam atrás dele.
 */
function SplitLayout({
  list,
  panel,
  footer,
}: {
  list: ReactNode;
  panel: ReactNode;
  footer: HTMLElement | null;
}) {
  const { ref, height } = useFitAboveFooter(footer);
  return (
    <div className="flex flex-wrap items-stretch gap-4">
      <div className="min-w-0 flex-[3_1_560px] self-start">{list}</div>
      <div
        ref={ref}
        style={{ height }}
        className="min-w-0 flex-[2_1_380px] self-start lg:sticky lg:top-4"
      >
        {panel}
      </div>
    </div>
  );
}

/** A oferta do roteiro pronto que espera a decisão do consultor, no lugar do console. */
interface PendingScriptOffer {
  node: OrgTreeNode;
  offer: OrgScriptOffer;
  /** `null` descarta a decisão (a abertura foi cancelada). */
  decide: (choice: OrgScriptChoice | null) => void;
}

function OrgExecution({
  projectId,
  blocked,
  footer,
  conversation,
  counts,
}: {
  counts?: { approved: number; completed: number };
  projectId: string;
  blocked: string | null;
  footer: HTMLElement | null;
  /** A conversa em voo neste nó (visão geral); `code` é a chave da unidade e `itemType` o nível dela. */
  conversation: ActiveConversation | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // A mesma consulta (e o mesmo cache) que a árvore lê: a vigia da fila precisa saber
  // quando a lista foi relida e o status do item no run da vez.
  const [selected, setSelected] = useState<OrgTreeNode | null>(null);
  const [canonical, setCanonical] = useState<OrgTreeNode[]>([]);
  const [liveRun, setLiveRun] = useState<{ key: string; runId: string } | null>(
    null,
  );
  const [queue, setQueue] = useState<ConfigQueue<OrgRunKey> | null>(null);
  const stopRequested = useRef(false);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queueStoppedAt, setQueueStoppedAt] = useState<string | null>(null);
  const opening = useRef(false);
  const watch = useRef<QueueWatch>(freshQueueWatch());
  const mounted = useRef(true);
  // A abertura da fila em voo: abortá-la descarta a oferta do roteiro que espera decisão.
  const queueOpening = useRef<AbortController | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      queueOpening.current?.abort();
    };
  }, []);
  const [scriptOffer, setScriptOffer] = useState<PendingScriptOffer | null>(
    null,
  );
  // As unidades que a fila deste "Run all" já cadastrou: o pai delas vale como cadastrado
  // para o filho antes de a árvore reler o status.
  const queueDone = useRef<Set<string>>(new Set());
  // O run que o consultor mandou pular ("Stop only this unit"): ao sair da visão geral a fila segue
  // para a próxima sem esperar o status final do item.
  const skippedRunId = useRef<string | null>(null);

  /**
   * Antes de abrir a conversa de uma unidade, pergunta ao servidor se há roteiro pronto.
   * Sem oferta (ou com a consulta falhando) abre como sempre, em `auto`. Com oferta, o
   * console mostra o painel e a conversa só abre depois do sim ou do não confirmado.
   * Na fila (`queued`, o "Run all") não há painel nem espera: com roteiro pronto a unidade
   * abre com ele (`script`, sem a LLM) e sem roteiro abre em `auto`, que é a conversa.
   */
  const requestScriptMode = useCallback(
    async (
      request: ScriptModeRequest,
      queued = false,
    ): Promise<OrgScriptMode | null> => {
      let offer: OrgScriptOffer;
      try {
        offer = await fetchOrgScriptOffer(
          projectId,
          request.unit,
          request.ancestors,
        );
      } catch (error) {
        // A oferta é conforto: sem ela a abertura segue o caminho de sempre.
        console.error("Falha ao consultar o roteiro pronto da unidade", error);
        return request.signal.aborted ? null : "auto";
      }
      if (request.signal.aborted) return null;
      if (!offer.available) return "auto";
      if (queued) return "script";
      return new Promise<OrgScriptMode | null>((resolve) => {
        const settle = (choice: OrgScriptChoice | null) => {
          request.signal.removeEventListener("abort", onAbort);
          setScriptOffer((current) =>
            current?.decide === settle ? null : current,
          );
          resolve(choice);
        };
        const onAbort = () => settle(null);
        request.signal.addEventListener("abort", onAbort);
        setScriptOffer({ node: request.node, offer, decide: settle });
      });
    },
    [projectId],
  );
  const selectedContext = useQuery({
    queryKey: [...stageKeys.all(projectId), "org-unit-context", selected?.key],
    queryFn: () => getOrgUnitContext(projectId, selected!.key),
    enabled: !!selected,
    ...LIST_QUERY_OPTIONS,
  });
  const selectedStatus = canonical.find(
    (candidate) => candidate.key === selected?.key,
  );
  const selectedDetails = selectedContext.data
    ? orgNodeFromContext(selectedContext.data)
    : selected;
  const node = selectedDetails
    ? {
        ...selectedDetails,
        migrationStatus: selectedStatus
          ? selectedStatus.migrationStatus
          : selectedDetails.migrationStatus,
        lastRunId: selectedStatus
          ? selectedStatus.lastRunId
          : selectedDetails.lastRunId,
      }
    : null;
  const conversationRunId = conversation?.runId;
  useEffect(() => {
    if (!conversation?.code || !conversation.itemType) return;
    void getOrgUnitContext(
      projectId,
      `${conversation.itemType}|${conversation.code}`,
    )
      .then((context) => {
        const next = orgNodeFromContext(context);
        next.lastRunId = conversation.runId;
        setSelected((current) => current ?? next);
      })
      .catch(() => {});
  }, [
    projectId,
    conversation?.code,
    conversation?.itemType,
    conversation?.runId,
  ]);
  // A conversa em voo é desta unidade pelo último run do item (o alias do D17 tem a chave
  // antiga) ou pela chave E pelo nível: chaves se repetem entre níveis raiz (§9.1b).
  const conversationRunOf = (target: OrgTreeNode | null): string | null =>
    conversation &&
    target &&
    (target.lastRunId === conversation.runId ||
      (conversation.itemType === target.levelCode &&
        conversation.code === target.itemKey))
      ? conversation.runId
      : null;
  const liveRunOf = (target: OrgTreeNode | null): string | null =>
    conversationRunOf(target) ??
    (target && liveRun?.key === target.key ? liveRun.runId : null);
  const displayedRunId = liveRunOf(node) ?? node?.lastRunId ?? null;
  const [conversationStates, setConversationStates] = useState<
    Record<string, ConversationStatus | null>
  >({});
  const onStatusChange = useCallback(
    (status: ConversationStatus | null) => {
      if (!displayedRunId || status === null) return;
      setConversationStates((current) =>
        current[displayedRunId] === status
          ? current
          : { ...current, [displayedRunId]: status },
      );
    },
    [displayedRunId],
  );
  const statusRunId =
    displayedRunId && displayedRunId in conversationStates
      ? displayedRunId
      : null;
  // A linha da unidade acompanha o console: conversa esperando a resposta não é "Running".
  const waitingRunIds = useMemo(
    () =>
      new Set(
        Object.entries(conversationStates)
          .filter(([, status]) => status === "waiting")
          .map(([runId]) => runId),
      ),
    [conversationStates],
  );
  const conversationStatus = statusRunId
    ? conversationStates[statusRunId]
    : null;
  const queuePosition = queue ? queueTotal - queue.remaining.length : null;
  // O SSE atualiza a conversa; a lista só é relida nas transições relevantes.
  useEffect(() => {
    if (statusRunId || queuePosition !== null) {
      void invalidateConfigQueries(queryClient, [
        ...stageKeys.all(projectId),
        "org-status",
      ]);
    }
  }, [statusRunId, conversationStatus, queuePosition, projectId, queryClient]);
  const previousConversationRunId = useRef(conversationRunId);
  const [inactiveRunIds, setInactiveRunIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // O terminal selecionado pode ser outro histórico quando a visão geral confirma o fim.
  useEffect(() => {
    const previous = previousConversationRunId.current;
    previousConversationRunId.current = conversationRunId;
    if (previous && previous !== conversationRunId) {
      setInactiveRunIds((current) => new Set([...current, previous]));
    }
  }, [conversationRunId]);
  const openRunId = conversation?.runId ?? liveRun?.runId ?? null;
  const persistedUnit = openRunId
    ? canonical.find((unit) => unit.lastRunId === openRunId)
    : undefined;
  const persistedEnded =
    persistedUnit &&
    (persistedUnit.migrationStatus === "completed" ||
      QUEUE_STOP_STATUSES.has(persistedUnit.migrationStatus));
  const live = Boolean(
    openRunId &&
      !inactiveRunIds.has(openRunId) &&
      !persistedEnded &&
      !isConversationEnded(conversationStates[openRunId] ?? null),
  );

  function stopQueueAt(unit: OrgRunKey) {
    setQueue(null);
    setQueueStoppedAt(unit.item_key);
  }

  // A consulta compacta entrega todas as unidades elegíveis em ordem pai→filho.
  async function runAll(_nodes: OrgTreeNode[]) {
    if (blocked || opening.current || queue) return;
    try {
      const result = await getOrgRunnableKeys(projectId);
      if (!result.items.length) return;
      stopRequested.current = false;
      queueDone.current = new Set();
      skippedRunId.current = null;
      setQueueStoppedAt(null);
      setQueueTotal(result.items.length);
      setQueue({ remaining: result.items, current: null });
    } catch {
      toast.error(t("Could not load this page's data."));
    }
  }
  const unitRefs = [
    ...new Map(
      [
        ...[...canonical, ...(selected ? [selected] : [])].map((node) => ({
          key: `${node.levelCode}|${node.itemKey}`,
          item_type: node.levelCode,
          item_key: node.itemKey,
        })),
        ...(queue?.current
          ? [
              {
                key: queue.current.entry.key,
                item_type: queue.current.entry.item_type,
                item_key: queue.current.entry.item_key,
              },
            ]
          : []),
      ].map((item) => [item.key, item]),
    ).values(),
  ];
  const orgStatuses = useQuery({
    queryKey: [...stageKeys.all(projectId), "org-status", unitRefs],
    queryFn: () =>
      getOrgStatuses(
        projectId,
        unitRefs.map(({ item_type, item_key }) => ({ item_type, item_key })),
      ),
    enabled: !!unitRefs.length,
    refetchInterval: live || !!queue?.current ? 3000 : false,
    ...LIST_QUERY_OPTIONS,
  });
  const statusByKey = useMemo(
    () =>
      new Map((orgStatuses.data?.items ?? []).map((item) => [item.key, item])),
    [orgStatuses.data],
  );
  useEffect(() => {
    if (!queue || queue.current || opening.current || blocked) return;
    const unit = queue.remaining[0];
    const remaining = queue.remaining.slice(1);
    if (!unit) {
      setQueue(null);
      return;
    }
    opening.current = true;

    const controller = new AbortController();
    queueOpening.current = controller;
    void getOrgUnitContext(projectId, unit.key)
      .then(async (context) => {
        if (
          !context.parent_registered &&
          !queueDone.current.has(unit.parent_key ?? "")
        )
          return { skip: true as const, opened: null, node: null };
        const node = orgNodeFromContext(context);
        setSelected(node);
        const mode = await requestScriptMode(
          {
            node,
            unit: context.unit,
            ancestors: context.ancestors,
            signal: controller.signal,
          },
          true,
        );
        if (mode === null) return { skip: false as const, opened: null, node };
        const opened = await openOrgUnitConversation(
          projectId,
          context.unit,
          context.ancestors,
          mode,
        );
        return { skip: false as const, opened, node };
      })
      .then(async ({ opened, node, skip }) => {
        if (!mounted.current) return;
        if (skip) {
          opening.current = false;
          setQueue((current) =>
            current && remaining.length
              ? { ...current, remaining, current: null }
              : null,
          );
          return;
        }
        if (!opened || !node) return;
        if (opened.resumed) {
          // Religou à conversa aberta do nó: se ela é de outra unidade, a fila para com o
          // aviso e o terminal não a mostra como desta unidade.
          const owner = await resolveOrgResumedOwner(projectId, node, opened);
          if (!mounted.current || controller.signal.aborted) return;
          if (!sameOrgUnit(owner, node)) {
            toast.info(
              otherConversationOpenText(
                t,
                owner ? effectiveOrgId(owner) : opened.activityCode,
              ),
            );
            stopQueueAt(unit);
            return;
          }
        }
        setLiveRun({ key: node.key, runId: opened.runId });
        if (stopRequested.current) {
          void orgStructureConversation
            .end(projectId, opened.runId)
            .catch((error: unknown) => {
              toast.error(
                conversationErrorText(t, conversationFailure(error).code),
              );
            });
          return;
        }
        watch.current = freshQueueWatch();
        setQueue(
          (current) =>
            current && {
              remaining,
              current: { entry: unit, runId: opened.runId },
            },
        );
        void invalidateConfigQueries(queryClient, stageKeys.all(projectId));
      })
      .catch((error: unknown) => {
        if (!mounted.current) return;
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.error("Unidade da fila removida", error);
          toast.error(
            t(
              "Unit {{code}} is no longer in the organizational structure. Reload the page and try again.",
              { code: unit.item_key },
            ),
          );
        } else toastOpenError(t, error, "org_structure", projectId);
        stopQueueAt(unit);
      })
      .finally(() => {
        opening.current = false;
        if (queueOpening.current === controller) queueOpening.current = null;
      });
  }, [queue, blocked, projectId, queryClient, requestScriptMode, t]);

  // A conversa da vez terminou? Espera ela sair da visão geral e o status do MESMO run:
  // "waiting" a mantém em voo. A lista relida dá o status pelo run (o alias do D17 tem
  // outra chave); a árvore canônica, pela unidade. A releitura da lista depois do fim é
  // pedida pela troca do `active_run` na visão geral (`ExecutionScreen`).
  const listedItems = orgStatuses.data?.items;
  const listReadAt = orgStatuses.dataUpdatedAt;
  useEffect(() => {
    const current = queue?.current;
    if (!current) return;
    const listed = listedItems?.find(
      (item) => item.last_run_id === current.runId,
    );
    const status = listed?.last_run_id === current.runId ? listed.status : null;
    const inFlight = conversationRunId === current.runId;
    const skipped = skippedRunId.current === current.runId && !inFlight;
    const verdict = skipped
      ? "advance"
      : queueTurnVerdict(watch.current, { inFlight, status, listReadAt });
    if (verdict === "advance") {
      if (status === "completed") queueDone.current.add(current.entry.key);
      setQueue((queued) => queued && { ...queued, current: null });
    } else if (verdict === "stop") stopQueueAt(current.entry);
  }, [queue, conversationRunId, canonical, listedItems, listReadAt]);

  // Só o run aberto pela fila: entre duas unidades não há o que encerrar, e a conversa
  // em voo na visão geral pode ser de outra aba. A abertura em voo é encerrada ao chegar.
  const stopQueue = () => {
    stopRequested.current = true;
    // Uma oferta de roteiro ainda sem resposta cai junto: não há conversa para abrir.
    queueOpening.current?.abort();
    const runId = queue?.current?.runId ?? null;
    setQueue(null);
    if (!runId) return;
    void orgStructureConversation
      .end(projectId, runId)
      .then(() => {
        void invalidateConfigQueries(queryClient, stageKeys.all(projectId));
      })
      .catch((error: unknown) => {
        toast.error(conversationErrorText(t, conversationFailure(error).code));
      });
  };

  // Parar a fila andando: escolhe-se entre só a unidade em voo (a fila segue para a próxima) ou
  // todas.
  const [stopChoice, setStopChoice] = useState(false);
  const skipCurrent = () => {
    const runId = queue?.current?.runId ?? null;
    if (!runId) return;
    skippedRunId.current = runId;
    void orgStructureConversation
      .end(projectId, runId)
      .then(() => {
        void invalidateConfigQueries(queryClient, stageKeys.all(projectId));
      })
      .catch((error: unknown) => {
        // O encerramento falhou: a unidade segue em voo e a fila a espera como antes.
        if (skippedRunId.current === runId) skippedRunId.current = null;
        toast.error(conversationErrorText(t, conversationFailure(error).code));
      });
  };
  const queueControl = { active: queue !== null, stopAll: stopQueue };

  return (
    <QueueControlContext.Provider value={queueControl}>
      <SplitLayout
        footer={footer}
        list={
          <>
            <OrgExecutionTree
              projectId={projectId}
              counts={counts}
              statusByKey={statusByKey}
              view="list"
              executionOnly
              live={live}
              waitingRunIds={waitingRunIds}
              runBlockedReason={blocked}
              onSelectNodeOverride={setSelected}
              activeNodeKey={node?.key ?? null}
              onRunStart={setSelected}
              onRunStateChange={() =>
                void invalidateConfigQueries(
                  queryClient,
                  stageKeys.all(projectId),
                )
              }
              onCanonicalNodesChange={setCanonical}
              onConversationOpened={(runId, openedNode) => {
                // A conversa religada pode ser de outra unidade: o console vai para a dona dela.
                setSelected(openedNode);
                setLiveRun({ key: openedNode.key, runId });
              }}
              onRunAll={runAll}
              runQueue={
                queue
                  ? {
                      kind: "running",
                      current: Math.min(
                        queueTotal,
                        queueTotal -
                          queue.remaining.length +
                          (queue.current ? 0 : 1),
                      ),
                      total: queueTotal,
                    }
                  : queueStoppedAt
                    ? { kind: "stopped", code: queueStoppedAt }
                    : null
              }
              onStopQueue={() => setStopChoice(true)}
              resolveScriptMode={requestScriptMode}
            />
          </>
        }
        panel={
          scriptOffer ? (
            <ScriptOfferPanel
              key={scriptOffer.node.key}
              unitType={t(
                orgUnitTypeLabelKey(
                  scriptOffer.node.unitTypeLabel,
                  scriptOffer.node.levelName,
                ),
                { defaultValue: scriptOffer.node.levelName },
              )}
              code={effectiveOrgId(scriptOffer.node)}
              version={scriptOffer.offer.version}
              onChoose={scriptOffer.decide}
            />
          ) : (
            <ExecutionPanel
              kind="org"
              onStatusChange={onStatusChange}
              projectId={projectId}
              node={node}
              liveRunId={liveRunOf(node)}
              parentNotRegistered={
                selectedContext.data?.parent_registered === false
              }
            />
          )
        }
      />
      <AlertDialog open={stopChoice} onOpenChange={setStopChoice}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Stop the queue?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "Stop only the unit that is running and go to the next, or stop all of them?",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={skipCurrent}>
              {t("Stop only this unit")}
            </AlertDialogAction>
            <AlertDialogAction onClick={stopQueue}>
              {t("Stop all")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </QueueControlContext.Provider>
  );
}

function ActivityExecution({
  footer,
  projectId,
  node,
  blocked,
  live,
  conversation,
  orderTip,
  counts,
}: {
  counts?: { approved: number; completed: number };
  footer: HTMLElement | null;
  projectId: string;
  node: ActivityNode;
  /** Execução em voo no projeto: trava o ▶, mas o terminal da conversa em voo segue religável. */
  blocked: string | null;
  /** Há execução em voo no projeto: a lista se relê sozinha. */
  live: boolean;
  /** A conversa em voo neste nó (visão geral), com a configuração dela. */
  conversation: ActiveConversation | null;
  /** Aviso da ordem ideal quando a aba anterior não terminou; não trava nada. */
  orderTip: {
    message: string;
    goTo: { label: string; onGo: () => void };
  } | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [q, commitSearch] = useDebouncedSearch(search);
  const flat = node === "module_config" || !!q.trim();
  const activities = useConfigActivities(
    projectId,
    node,
    { q: q || undefined },
    flat,
  );
  const areas = useExecutionActivityAreas(projectId, node, !flat);
  const [areaRows, setAreaRows] = useState<Record<string, ConfigActivityRow[]>>(
    {},
  );
  const registerRows = useCallback(
    (area: string, rows: ConfigActivityRow[]) =>
      setAreaRows((current) =>
        current[area] === rows ? current : { ...current, [area]: rows },
      ),
    [],
  );
  const [runnable, setRunnable] = useState<RunnableCodesResponse | null>(null);
  // O selo do GROW vem do backend: URL e client do tenant Configuration e o login pronto (o do CBC
  // testado, ou o próprio da Configuration com o checkbox desmarcado). A regra mora lá, para a tela
  // e a recusa do ▶ concordarem.
  const grow = useTargetConnection(projectId, "configuration");
  const growConfigured = grow.data ? Boolean(grow.data.ready) : undefined;
  const cbcNotTested = grow.data?.missing === "cbc_not_tested";
  // Sem o tenant do GROW a conversa quebra: trava o ▶ de todas as linhas e o "Run all". Além dele, a
  // linha trava sozinha sem extração e, na SSCUI, sem link para o SAP.
  const growMissing =
    growConfigured === false
      ? cbcNotTested
        ? t(
            "Test your CBC login in Connections, or use your own Configuration login to run",
          )
        : t("Configure the GROW connection to run")
      : null;
  const [growDialogOpen, setGrowDialogOpen] = useState(false);
  useEffect(() => {
    if (growDialogOpen && growConfigured) setGrowDialogOpen(false);
  }, [growDialogOpen, growConfigured]);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  // O run aberto pelo ▶ fica preso à sua configuração: trocar de linha não o mostra em outra.
  const [liveRun, setLiveRun] = useState<{
    code: string;
    runId: string;
  } | null>(null);
  const [details, setDetails] = useState<ConfigActivityEntry | null>(null);
  const starting = useRef(false);
  const [queue, setQueue] = useState<ConfigQueue<string> | null>(null);
  const [queueStoppedAt, setQueueStoppedAt] = useState<string | null>(null);
  const opening = useRef(false);
  const watch = useRef<QueueWatch>(freshQueueWatch());
  const loaded = useMemo(
    () =>
      flat
        ? (activities.data?.pages.flatMap((page) => page.items) ?? [])
        : Object.values(areaRows).flat(),
    [flat, activities.data, areaRows],
  );
  const statusCodes = [
    ...new Set([
      ...loaded.map((row) => row.activity_code),
      ...(queue?.current ? [queue.current.entry] : []),
      ...(selectedCode ? [selectedCode] : []),
    ]),
  ];
  const statuses = useActivityStatuses(
    projectId,
    node,
    statusCodes,
    live || !!queue?.current,
  );
  const statusByCode = useMemo(
    () =>
      new Map(
        (statuses.data?.items ?? []).map((item) => [item.activity_code, item]),
      ),
    [statuses.data],
  );
  const entries = useMemo(
    () =>
      loaded.map((row) => ({ ...row, ...statusByCode.get(row.activity_code) })),
    [loaded, statusByCode],
  );
  const row =
    entries.find((candidate) => candidate.activity_code === selectedCode) ??
    null;
  // Tela aberta com uma conversa em voo neste nó: a linha dela é selecionada e o
  // terminal religa, uma vez por conversa (trocar de linha depois não é desfeito).
  const conversationCode = conversation?.code ?? null;
  const conversationRunId = conversation?.runId ?? null;
  const adoptedRun = useRef<string | null>(null);
  useEffect(() => {
    if (
      !conversationCode ||
      !conversationRunId ||
      adoptedRun.current === conversationRunId
    )
      return;
    adoptedRun.current = conversationRunId;
    setSelectedCode(conversationCode);
    setLiveRun({ code: conversationCode, runId: conversationRunId });
  }, [conversationCode, conversationRunId]);

  /** Abre (ou religa) a conversa da configuração; devolve o run dela, ou `null` se não abriu. */
  async function run(code: string): Promise<string | null> {
    // Sem o GROW configurado a conversa nunca abre, por nenhum caminho.
    if (growMissing || starting.current) return null;
    starting.current = true;
    setSelectedCode(code);
    try {
      const opened = await openNodeConversation(projectId, node, code);
      void invalidateConfigQueries(queryClient, stageKeys.overview(projectId));
      void invalidateConfigQueries(queryClient, [
        ...stageKeys.all(projectId),
        "activity-status",
        node,
      ]);
      if (opened.resumed && opened.activityCode !== code) {
        // O ▶ religou à conversa que já estava aberta, de outra configuração: o terminal
        // mostra a linha dela, nunca a clicada, para ninguém responder à conversa errada.
        toast.info(otherConversationOpenText(t, opened.activityCode));
        if (opened.activityCode === null) return null;
        setSelectedCode(opened.activityCode);
        setLiveRun({ code: opened.activityCode, runId: opened.runId });
        return null;
      }
      setLiveRun({ code, runId: opened.runId });
      return opened.runId;
    } catch (error) {
      // A recusa chega com o código no `detail`; a tela mostra a frase, nunca o código.
      toastOpenError(t, error, node, projectId);
      return null;
    } finally {
      starting.current = false;
    }
  }
  const runRef = useRef(run);
  runRef.current = run;

  function stopQueueAt(code: string) {
    setQueue(null);
    setQueueStoppedAt(code);
  }

  // Abre a próxima, uma por vez: nunca com outra execução no projeto nem sem o GROW.
  useEffect(() => {
    if (!queue || queue.current || opening.current || blocked || growMissing)
      return;
    const [code, ...rest] = queue.remaining;
    if (!code) {
      setQueue(null);
      return;
    }
    opening.current = true;
    void runRef.current(code).then((runId) => {
      opening.current = false;
      if (!runId) {
        stopQueueAt(code);
        return;
      }
      watch.current = freshQueueWatch();
      setQueue(
        (current) =>
          current && { remaining: rest, current: { entry: code, runId } },
      );
    });
  }, [queue, blocked, growMissing]);

  // A conversa da vez terminou? Ver `queueTurnVerdict`.
  const listReadAt = statuses.dataUpdatedAt;
  useEffect(() => {
    const current = queue?.current;
    if (!current) return;
    const entry = statusByCode.get(current.entry);
    // Só a linha relida depois da abertura (com o run dela) diz como a conversa acabou.
    const status =
      entry && entry.last_run_id === current.runId ? entry.status : null;
    const verdict = queueTurnVerdict(watch.current, {
      inFlight: conversationRunId === current.runId,
      status,
      listReadAt,
    });
    if (verdict === "advance")
      setQueue((queued) => queued && { ...queued, current: null });
    else if (verdict === "stop") stopQueueAt(current.entry);
  }, [queue, conversationRunId, statusByCode, listReadAt]);

  const runAllDisabledReason = growMissing ?? blocked;
  const listQuery = flat ? activities : areas;

  const list =
    listQuery.isError && !listQuery.data ? (
      <div className={`${PANEL_CLASS} p-4`}>
        <ConfigServerAlert
          message={t("Could not load this page's data.")}
          onRetry={() => void listQuery.refetch()}
        />
      </div>
    ) : !listQuery.data ? (
      <div className={cn(PANEL_CLASS, "p-4")}>
        <StageLoading variant="list" />
      </div>
    ) : (
      <>
        {listQuery.isError && listQuery.data && (
          <div role="alert">
            <button
              type="button"
              onClick={() => void activities.fetchNextPage()}
            >
              {t("Retry")}
            </button>
          </div>
        )}
        <ConfigActivityList
          rows={entries}
          search={search}
          onSearchChange={setSearch}
          onSearchEnter={commitSearch}
          done={counts?.completed ?? 0}
          total={counts?.approved ?? 0}
          hasMore={flat && !!activities.hasNextPage}
          loadingMore={flat && activities.isFetchingNextPage}
          onLoadMore={flat ? () => void activities.fetchNextPage() : undefined}
          body={
            !flat ? (
              <>
                {areas.data?.items.map((area) => (
                  <ExecutionActivityArea
                    key={area.application_area ?? ""}
                    projectId={projectId}
                    node={node}
                    area={area.application_area ?? null}
                    total={area.approved}
                    onRows={registerRows}
                    statuses={statusByCode}
                    props={{
                      onDetails: setDetails,
                      onRun: (code) => void run(code),
                      runDisabledReason: growMissing ?? blocked,
                      activeCode: selectedCode,
                      onSelect: setSelectedCode,
                      conversationCode,
                      showLaunchLink: node === "sscui",
                    }}
                  />
                ))}
              </>
            ) : undefined
          }
          title={t(
            node === "sscui"
              ? "SSCUI configurations"
              : "App Fiori configurations",
          )}
          notice={
            queueStoppedAt && (
              <p
                role="status"
                className="mx-5 mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-[#8a5200]"
              >
                {t("Queue stopped at {{code}}", { code: queueStoppedAt })}
              </p>
            )
          }
          onDetails={setDetails}
          onRun={(code) => void run(code)}
          runDisabledReason={growMissing ?? blocked}
          activeCode={selectedCode}
          onSelect={setSelectedCode}
          conversationCode={conversationCode}
          showLaunchLink={node === "sscui"}
          growConfigured={growConfigured}
          growUrl={grow.data?.base_url}
          projectId={projectId}
          onConfigureGrow={() => setGrowDialogOpen(true)}
          toolbarActions={
            <ConfigRunAllControls
              count={runnable?.codes.length ?? 0}
              skipped={runnable?.not_extracted_count ?? 0}
              disabledReason={runAllDisabledReason}
              queueRunning={queue !== null}
              onPrepare={async () => {
                const result = await listRunnableActivityCodes(projectId, node);
                setRunnable(result);
              }}
              onConfirm={() => {
                setQueueStoppedAt(null);
                setQueue({ remaining: runnable?.codes ?? [], current: null });
              }}
              onStop={() => setQueue(null)}
            />
          }
        />
      </>
    );

  return (
    <>
      {growConfigured === false && (
        <StageAlert
          message={
            cbcNotTested
              ? t(
                  "Test your CBC login in Connections, or use your own Configuration user and password.",
                )
              : t("Connect to GROW to start running the agent.")
          }
          action={{
            label: t("Click here to connect"),
            onClick: () => setGrowDialogOpen(true),
          }}
        />
      )}
      {orderTip && (
        <StageAlert
          message={orderTip.message}
          action={{
            label: t("Go to {{stage}}", { stage: orderTip.goTo.label }),
            onClick: orderTip.goTo.onGo,
            arrow: true,
          }}
        />
      )}
      <SplitLayout
        footer={footer}
        list={list}
        panel={
          <ExecutionPanel
            kind="activity"
            projectId={projectId}
            node={node}
            row={row}
            activityCode={selectedCode}
            statusRunId={
              selectedCode ? statusByCode.get(selectedCode)?.last_run_id : null
            }
            liveRunId={
              liveRun && liveRun.code === selectedCode ? liveRun.runId : null
            }
          />
        }
      />
      <GrowConnectionDialog
        projectId={projectId}
        open={growDialogOpen}
        onOpenChange={setGrowDialogOpen}
      />
      {details && (
        <SscuiActivityModal
          open
          onClose={() => setDetails(null)}
          projectId={projectId}
          node={node}
          activityCode={details.activity_code}
          meta={{
            name: details.name ?? null,
            priority: details.priority ?? null,
            countries: details.countries ?? [],
            scopeItemList: details.scope_items?.map((item) => ({
              id: item.id,
              name: item.name ?? null,
            })),
          }}
        />
      )}
    </>
  );
}
